import { ReactNode, useEffect } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";

import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { Label } from "src/base-components/Label";
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { FormField } from "src/cases/caseView/components/FormField";
import {
  CaseTask,
  TaskCheckbox,
} from "src/cases/caseView/components/TaskCheckbox";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import { CaseTemplateFormFieldsInner } from "src/clients/case-management";

type FloatingFormTitleProps = {
  title: string;
  description?: string | null;
  task: CaseTask;
  caseData?: CaseDeserialized;
  disabled?: boolean;
};

export const FloatingFormTitle: React.FC<FloatingFormTitleProps> = ({
  title,
  description,
  task,
  caseData,
  disabled,
}) => (
  <div className="flex min-w-0 flex-col gap-y-1" data-grabbable>
    <div className="flex min-w-0 gap-x-2" data-grabbable>
      <div className="pt-0.5">
        <TaskCheckbox caseData={caseData} disabled={disabled} task={task} />
      </div>
      <div className="min-w-0" data-grabbable>
        <p className="truncate font-inter-semibold-13px text-gray-800">
          {title}
        </p>
        {description && (
          <div
            //skipcq: JS-0440
            dangerouslySetInnerHTML={{ __html: mdService.toHtml(description) }}
            className="tktl-markdown font-inter-normal-12px text-gray-500"
          />
        )}
      </div>
    </div>
  </div>
);

type FloatingFormWindowProps = {
  title: ReactNode;
  fields: CaseTemplateFormFieldsInner[];
  defaultValues: Record<string, unknown>;
  schema: yup.ObjectSchema<Record<string, unknown>>;
  isOpen: boolean;
  disabled?: boolean;
  button?: ReactNode;
  titleRight?: ReactNode;
  isPending?: boolean;
  onSaveAndClose: () => Promise<void> | void;
  onClose: () => void;
  onFormChange?: (
    values: Record<string, unknown>,
    isDirty: boolean,
    isValid: boolean,
  ) => void;
};

export const FloatingFormWindow: React.FC<FloatingFormWindowProps> = ({
  title,
  fields,
  defaultValues,
  schema,
  isOpen,
  disabled = false,
  button,
  titleRight,
  isPending = false,
  onSaveAndClose,
  onClose,
  onFormChange,
}) => {
  const form = useForm<Record<string, unknown>>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { isDirty, isValid } = form.formState;

  useEffect(() => {
    form.reset(defaultValues, { keepDirty: false, keepTouched: false });
  }, [defaultValues, form]);

  useEffect(() => {
    if (!onFormChange) return;

    const subscription = form.watch((values) => {
      onFormChange(
        values as Record<string, unknown>,
        form.formState.isDirty,
        form.formState.isValid,
      );
    });

    return () => subscription.unsubscribe();
  }, [form, onFormChange]);

  const canSave = !isPending && isDirty && isValid;

  return (
    <FloatingWindow
      button={button}
      headerAlignment="start"
      isOpen={isOpen}
      maximizable={false}
      placement="bottom"
      title={title}
      titleRight={titleRight}
      draggable
      resizeable
      onClose={onClose}
    >
      <div className="relative -mx-2 flex h-full flex-col justify-between">
        <div className="decideScrollbar p-4">
          <FormProvider {...form}>
            <form className="flex flex-col gap-y-3">
              {fields.map((field) => (
                <div key={field.key}>
                  <Label
                    mb={field.description ? "mb-0" : "mb-2"}
                    required={field.required}
                  >
                    {field.display_name}
                  </Label>

                  {field.description && (
                    <div
                      //skipcq: JS-0440
                      dangerouslySetInnerHTML={{
                        __html: mdService.toHtml(field.description),
                      }}
                      className="tktl-markdown mb-2 font-inter-normal-12px text-gray-500"
                    />
                  )}

                  <FormField disabled={disabled} field={field} />

                  {form.formState.errors?.[field.key] && (
                    <ErrorHint>
                      {form.formState.errors[field.key]?.message}
                    </ErrorHint>
                  )}
                </div>
              ))}
            </form>
          </FormProvider>
        </div>

        <div>
          <div className="border-gray-100 border-t" />
          <div className="flex justify-end gap-2 px-4 pt-4 pb-2">
            <Button
              disabled={!canSave}
              variant="primary"
              onClick={onSaveAndClose}
            >
              {isPending ? "Saving..." : "Save and Close"}
            </Button>
          </div>
        </div>
      </div>
    </FloatingWindow>
  );
};
